import React from "react";
import { Info, Check, X, Loader, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import SearchableSelect from "../SearchableSelect";

export default function AdjustmentTable({
  data,
  loading,
  error,
  onInfo,
  onApprove,
  onReject,
  onPageChange,
  currentPage,
  itemsPerPage,
  totalEntries,
  startEntry,
  endEntry,
  maxPages,
  onItemsPerPageChange,
  onSort,
  sortBy,
  sortOrder,
}) {
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "REJECTED":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "DRAFT":
      case "SUBMITTED":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "Disetujui";
      case "REJECTED":
        return "Ditolak";
      case "DRAFT":
      case "SUBMITTED":
        return "Diajukan";
      default:
        return status || "-";
    }
  };

  const getAdjustmentTypeLabel = (type) => {
    const t = (type || "").toUpperCase();
    if (t === "IN" || t === "STOCK_IN") return "Stok Masuk";
    if (t === "OUT" || t === "STOCK_OUT") return "Stok Keluar";
    return type || "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return (
        new Intl.DateTimeFormat("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(date) +
        ", " +
        new Intl.DateTimeFormat("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        }).format(date)
      );
    } catch {
      return dateString;
    }
  };

  const flatRows = React.useMemo(() => {
    const out = [];
    (data || []).forEach((adj) => {
      out.push({
        adjustmentId: adj.id,
        rowKey: `adj-${adj.id}`,
        status: adj.status,
        createdAt: adj.created_at || adj.date,
        reason: adj.reason || adj.notes || "-",
        createdBy:
          adj.created_by_name ||
          adj.adjusted_by_name ||
          adj.created_by ||
          "-",
      });
    });
    return out;
  }, [data]);

  const SortIcon = ({ column }) => {
    if (sortBy !== column)
      return <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 ml-1" />;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3.5 h-3.5 text-teal-600 ml-1" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-teal-600 ml-1" />
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mt-6">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-700">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-36">
                    Aksi
                  </th>
                  <th
                    onClick={() => onSort && onSort("status")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none">
                    <span className="inline-flex items-center">
                      Status <SortIcon column="status" />
                    </span>
                  </th>
                  <th
                    onClick={() => onSort && onSort("date")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none">
                    <span className="inline-flex items-center">
                      Tanggal Dibuat <SortIcon column="date" />
                    </span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Keterangan Penyesuaian
                  </th>
                  <th
                    onClick={() => onSort && onSort("created_by")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none">
                    <span className="inline-flex items-center">
                      Dibuat oleh <SortIcon column="created_by" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {flatRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-8 text-center text-gray-500">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  flatRows.map((row) => {
                    const isPending =
                      (row.status || "").toUpperCase() === "DRAFT" ||
                      (row.status || "").toUpperCase() === "SUBMITTED";
                    return (
                      <tr
                        key={row.rowKey}
                        className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => onInfo && onInfo(row.adjustmentId)}
                              className="w-8 h-8 bg-sky-500 text-white rounded-lg hover:bg-sky-600 flex items-center justify-center transition-colors shadow-sm shadow-sky-500/20"
                              title="Detail">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() =>
                                isPending &&
                                onReject &&
                                onReject(row.adjustmentId)
                              }
                              disabled={!isPending || !onReject}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                isPending && onReject
                                  ? "bg-rose-500 text-white hover:bg-rose-600 shadow-sm shadow-rose-500/20"
                                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
                              }`}
                              title={isPending ? "Tolak" : "Tidak tersedia"}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() =>
                                isPending &&
                                onApprove &&
                                onApprove(row.adjustmentId)
                              }
                              disabled={!isPending || !onApprove}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                isPending && onApprove
                                  ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm shadow-emerald-500/20"
                                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
                              }`}
                              title={isPending ? "Setujui" : "Tidak tersedia"}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-block px-3 py-1 rounded-full border text-xs font-medium whitespace-nowrap ${getStatusColor(row.status)}`}>
                            {getStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                          <span className="line-clamp-2" title={row.reason}>
                            {row.reason || "-"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                          {row.createdBy}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {startEntry} to {endEntry} of {totalEntries} entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
